import { useRef } from 'react';
import type { ISectionCost } from '@services/types';

import { E_DEVICE_TYPE } from '@common/configs';
import { classesBuilder, formatNumber } from '@common/helpers';
import Card from '@components/Card';
import DeviceFrame from '@components/DeviceFrame';
import { useSharedDeviceScale } from '@components/DeviceFrame/hooks';
import Icon from '@components/Icon';
import type { ISectionComponentProps } from '@components/ReportContent/types';

import '../section.scss';
import style from './styles.module.scss';

const CostSection = ({ sectionData }: ISectionComponentProps) => {
  const sharedContainerRef = useRef<HTMLDivElement>(null);
  const { desktop } = useSharedDeviceScale(sharedContainerRef);

  // Checking that data is Cost-Data
  if (!('data' in sectionData) || !('tkp' in sectionData.data)) {
    return <div>Error: Invalid data for Cost section.</div>;
  }

  const { data, images } = sectionData as ISectionCost;

  return (
    <div className={classesBuilder('section-wrapper', style['cost-wrapper'])}>
      <div className="section-header">
        <h3 className="section-subtitle">Campaign Review</h3>
        <h1 className="section-title">MEDIA COST</h1>
      </div>

      {images?.cost_device_desktop && (
        <div className="section-image">
          <div
            className={style['preview-wrapper']}
            ref={sharedContainerRef}
          >
            <div className={style['device-container']}>
              <DeviceFrame
                deviceType={E_DEVICE_TYPE.DESKTOP}
                imageSrc={images.cost_device_desktop}
                sharedScale={desktop}
              />
            </div>
          </div>
        </div>
      )}

      <div className={classesBuilder('section-data', style['cost-data'])}>
        <Card>
          <section className={style['cost-block']}>
            <h2 className={style['cost-header-title']}>Total Media Spend</h2>
            <div className={style['cost-metrics']}>
              <div className={style['metric-item']}>
                <div className={style['cost-metric-headline']}>
                  <Icon name="money" />
                  <span className={style['cost-metric-label']}>Total Media Spend</span>
                </div>
                <p className={style['cost-metric-value']}>{formatNumber(data.total_media_spend)}</p>
              </div>

              <div className={classesBuilder(style.divider, style['mobile-only'])} />

              <div className={style['metric-item']}>
                <div className={style['cost-metric-headline']}>
                  <Icon name="eye" />
                  <span className={style['cost-metric-label']}>Paid Ad Impressions</span>
                </div>
                <p className={style['cost-metric-value']}>{formatNumber(data.paid_ad_impressions)}</p>
              </div>

              <div className={classesBuilder(style.divider, style['desktop-only'])} />

              <div className={style['metric-item']}>
                <div className={style['cost-metric-headline']}>
                  <Icon name="money" />
                  <span className={style['cost-metric-label']}>TKP (N2)</span>
                </div>
                <p className={style['cost-metric-value']}>{formatNumber(data.tkp, { style: 'currency' })}</p>
              </div>
            </div>
          </section>
        </Card>

        <Card>
          <section className={style['cost-block']}>
            <h2 className={style['cost-header-title']}>Clicks</h2>
            <div className={style['cost-metrics']}>
              <div className={style['metric-item']}>
                <div className={style['cost-metric-headline']}>
                  <Icon name="click" />
                  <span className={style['cost-metric-label']}>Clicks</span>
                </div>
                <p className={style['cost-metric-value']}>{formatNumber(data.clicks)}</p>
              </div>

              <div className={classesBuilder(style.divider, style['mobile-only'])} />

              <div className={style['metric-item']}>
                <div className={style['cost-metric-headline']}>
                  <Icon name="money" />
                  <span className={style['cost-metric-label']}>Effective CPC (N2)</span>
                </div>
                <p className={style['cost-metric-value']}>{formatNumber(data.effective_cpc, { style: 'currency' })}</p>
              </div>
            </div>
          </section>
        </Card>

        <Card>
          <section className={style['cost-block']}>
            <h2 className={style['cost-header-title']}>Engagements</h2>
            <div className={style['cost-metrics']}>
              <div className={style['metric-item']}>
                <div className={style['cost-metric-headline']}>
                  <Icon name="click" />
                  <span className={style['cost-metric-label']}>Engagements</span>
                </div>
                <p className={style['cost-metric-value']}>{formatNumber(data.engagements)}</p>
              </div>

              <div className={classesBuilder(style.divider, style['mobile-only'])} />

              <div className={style['metric-item']}>
                <div className={style['cost-metric-headline']}>
                  <Icon name="money" />
                  <span className={style['cost-metric-label']}>Effective CPE (N2)</span>
                </div>
                <p className={style['cost-metric-value']}>{formatNumber(data.effective_cpe, { style: 'currency' })}</p>
              </div>
            </div>
          </section>
        </Card>
      </div>
    </div>
  );
};

export default CostSection;
